/*
 * @Author: alex
 * @Date: 2022-11-14 09:19:11
 * @Description:
 * Copyright (c) 2022 by piesat, All Rights Reserved.
 */
import * as ol from 'ol'

import 'ol/ol.css'
import { mapContext } from './mapContext'
import { info } from './console'
import { BaseMapTool, MapIdentifyTool } from './tools'
import { TypeEvented, isString, assertTrue } from '@vmap/core'
import type { MapStyle, MapStyleSource } from './mapSpecification'
import { MapLayerLoader, LayerTypes } from './layers/mapLayerLoader'
import { fromLonLat, transformExtent } from 'ol/proj'
import type { Layer } from 'ol/layer'
import { EPSG3857, EPSG4326, hasRegistered } from './projection/mapProjection'
import MouseWheelZoom from 'ol/interaction/MouseWheelZoom'
import DoubleClickZoom from 'ol/interaction/DoubleClickZoom'
import DragAndDrop from 'ol/interaction/DragAndDrop'
import PointerInteraction from 'ol/interaction/Pointer'
import { ShapfileLayer } from './layers/shpLayer'

/**
 * 地图事件载荷数据类型
 */
interface MapEventPayload {
  map: ol.Map
  container: MapContainer
  type: string
}

/**
 * 地图事件
 */
interface MapEvent {
  // 地图样式文件加载完成
  map_style_loaded: MapEventPayload
  // 地图初始化完成
  map_loaded: MapEventPayload
  map_loading: { mc: MapContainer }
}

export class MapContainer extends TypeEvented<MapEvent> {
  /**
   * 地图对象挂载的html对象id
   */
  #target: string

  /**
   * olmap
   */
  #map!: ol.Map

  #style!: MapStyle

  #tools: Map<string, BaseMapTool<any>> = new Map()

  constructor(target: string) {
    super()
    this.#target = target
  }

  get target() {
    return this.#target
  }

  get map() {
    return this.#map
  }

  get style() {
    return this.#style
  }

  /**
   * 加载地图模板
   * <code>
   * <pre>
   * mapContainer.load(async () => {
   *    const data: MapStyle = await axios.get('map_style.json')
   *    return data
   * })
   * </pre>
   * </code>
   *
   *
   * @param styleSource 加载的style文件名
   */
  load = async (styleSource: MapStyleSource) => {
    if (typeof styleSource === 'function') {
      const data = await styleSource()
      this.initMapView(data)
    } else if (isString(styleSource)) {
      fetch(styleSource as string)
        .then(res => res.json())
        .then(res => {
          const tplData: MapStyle = res
          this.initMapView(tplData)
        })
        .catch(ex => {
          console.error(ex)
        })
    } else {
      this.initMapView(styleSource as MapStyle)
    }
  }

  remove() {
    console.log('TODO...')
  }

  /**
   * 获取图层
   * @param id 图层id
   * @returns
   */
  getLayerById(id: string): Layer | undefined {
    const layers = this.#map.getAllLayers()
    return layers.find(lyr => lyr.get('id') === id)
  }

  /**
   * 调整图层的透明度
   * @param lyrId 图层id
   * @param opacity 需要设置的透明度
   * @param callback 回调函数
   */
  setLayerOpacity(
    lyrId: string,
    opacity: number,
    callback?: (pre: number, now: number) => void
  ): void {
    const lyr = this.getLayerById(lyrId)
    if (lyr && lyr?.getVisible()) {
      const pre = lyr.getOpacity()
      lyr.setOpacity(opacity)
      if (callback) {
        callback(pre, lyr.getOpacity())
      }
    }
  }

  /**
   * 获取地图工具
   * @param type 工具类型
   * @returns 地图工具
   */
  getTool<T extends BaseMapTool<any>>(type: 'identify' | string): T {
    return this.#tools.get(type) as T
  }

  /**
   * 开/关 地图交互
   * @param type 地图键鼠交互类型
   * @param toggle 开/关
   */
  toggleMapInteraction(
    type:
      | 'MouseWheelZoom'
      | 'DoubleClickZoom'
      | 'DragAndDrop'
      | 'PointerInteraction',
    toggle = false
  ) {
    switch (type) {
      case 'MouseWheelZoom':
        {
          const interaction = this.#map
            .getInteractions()
            .getArray()
            .find(interaction => {
              return interaction instanceof MouseWheelZoom
            })
          interaction?.setActive(toggle)
        }
        break
      case 'DoubleClickZoom':
        {
          const interaction = this.#map
            .getInteractions()
            .getArray()
            .find(interaction => {
              return interaction instanceof DoubleClickZoom
            })
          interaction?.setActive(toggle)
        }
        break
      case 'DragAndDrop':
        {
          const interaction = this.#map
            .getInteractions()
            .getArray()
            .find(interaction => {
              return interaction instanceof DragAndDrop
            })
          interaction?.setActive(toggle)
        }
        break
      case 'PointerInteraction':
        {
          const interaction = this.#map
            .getInteractions()
            .getArray()
            .find(interaction => {
              return interaction instanceof PointerInteraction
            })
          interaction?.setActive(toggle)
        }
        break
    }
  }

  /**
   * 初始化地图视图
   * @param styleData 地图模板
   */
  private initMapView(styleData: MapStyle) {
    console.log('init map view...')

    this.#style = styleData
    info('传入的地图样式', styleData)
    mapContext.setMapStyle(styleData)

    const layerLoader = new MapLayerLoader(styleData)
    const configLayers = layerLoader.load()
    assertTrue(
      hasRegistered(styleData.crs ?? EPSG3857),
      `[${styleData.crs}] is not registered yet`
    )
    // FIXME: 过滤出需要延迟加载的 图层
    const initialLayers = configLayers.filter(
      lyr => lyr.type !== LayerTypes.Shp
    )
    const lazyLayers = configLayers.filter(lyr => lyr.type === LayerTypes.Shp)

    // center： 从经纬度转换为地图指定坐标系
    const view = new ol.View({
      projection: styleData.crs,
      // FIXME: 这里修改验证配置里的坐标系
      center: fromLonLat(styleData.center), //styleData.crs
      zoom: styleData.zoom,
      rotation: styleData.bearing
    })
    this.#map = new ol.Map({
      layers: initialLayers,
      controls: [],
      target: this.#target,
      view: view
    })
    // 加载 shp layer
    lazyLayers.forEach(async lyr => {
      if (lyr instanceof ShapfileLayer) {
        const mapLayer = await lyr.toMapLayer()
        this.#map.addLayer(mapLayer)
      }
    })

    const initialExtent = styleData.initialExtent
    if (initialExtent !== undefined) {
      // initialExtent: 若坐标系不一致，则进行转换
      const extentCrs = initialExtent.crs ?? EPSG4326
      if (styleData.crs !== extentCrs) {
        view.fit(
          transformExtent(initialExtent.coordinates, extentCrs, styleData.crs),
          {
            nearest: true
          }
        )
      } else {
        view.fit(initialExtent.coordinates, { nearest: true })
      }
    }

    this.#map.once('loadend', () => {
      // 数据共享
      mapContext.setMainMap(this.#map)
      this.fire('map_loaded', {
        map: this.#map,
        container: this,
        type: 'map_loaded'
      })
      // 添加全局的tools
      this.#tools.set('identify', new MapIdentifyTool(this.#map))
      this.#tools.forEach(value => value.start())

      // 用于全局事件
      mapContext.fire('map_ready', { map: this.#map })
    })

    this.#map.once('loadstart', () => {
      // set mc
      mapContext.setMapContainer(this)

      // 创建地图控件容器 所有的自定义的control 都会被加入到此容器中
      // const viewPort: HTMLDivElement = document.getElementsByClassName(
      //   'ol-viewport'
      // )[0] as HTMLDivElement

      // const controlContainer = this.createControlDiv('map-control-container')
      // viewPort.appendChild(controlContainer)

      // const controlContainerCustom = this.createControlDiv(
      //   'map-control-container-custom'
      // )
      // viewPort.parentElement?.appendChild(controlContainerCustom)

      this.fire('map_loading', { mc: this })
    })
  }

  // /**
  //  * 创建 div
  //  *
  //  * @param baseClsName 基础样式名称
  //  * @returns
  //  */
  // private createControlDiv(baseClsName: string): HTMLDivElement {
  //   const div: HTMLDivElement = document.createElement('div')
  //   div.className = baseClsName

  //   const top: HTMLDivElement = document.createElement('div')
  //   top.className = `${baseClsName}-top`
  //   const bottom: HTMLDivElement = document.createElement('div')
  //   bottom.className = `${baseClsName}-bottom`

  //   top.appendChild(this.createEl('top', 'left', baseClsName))
  //   top.appendChild(this.createEl('top', 'center', baseClsName))
  //   top.appendChild(this.createEl('top', 'right', baseClsName))

  //   bottom.appendChild(this.createEl('bottom', 'left', baseClsName))
  //   bottom.appendChild(this.createEl('bottom', 'center', baseClsName))
  //   bottom.appendChild(this.createEl('bottom', 'right', baseClsName))

  //   div.appendChild(top)
  //   div.appendChild(bottom)
  //   return div
  // }

  // private createEl(
  //   vertical: 'top' | 'bottom',
  //   horizontal: 'left' | 'center' | 'right',
  //   clsPrefix: string
  // ): HTMLDivElement {
  //   const div: HTMLDivElement = document.createElement('div')
  //   // div.classList.add(`map-control-wrapper-${vertical}`)
  //   div.classList.add(`${clsPrefix}-${vertical}-${horizontal}`)
  //   return div
  // }
}
